import { authFetchGET, authFetchPOST, authFetchPUT } from '../utils/auth-fetch';
import { VesselPositionData, VesselPositionDataExtended, VesselTrackData } from './types/position-service.types';

const env = (<any>window)["appenv"].services.positionsapi;

export class PositionsService {

    // Gets the most recent position of multiple vessels.
    static async currentPosition_VesselVoyage(imoNos: number[]): Promise<VesselPositionData[]> {
        // Size of URL is limited to 8000 bytes, so fetch 100 vessels at a time, more than that and API response is inconsistent
        const imoBatches = [];
        for (let i = 0; i < imoNos.length; i += 100) {
            imoBatches.push(imoNos.slice(i, i + 100));
        }

        const promises = imoBatches.map(imoBatch => {
            const imoQuery = imoBatch.map(imo => "imo=" + imo).join("&");
            return authFetchGET(env.scope, `${env.baseurl}/positions/latest?${imoQuery}`);
        });

        const results = await Promise.all(promises);
        const data: any[] = (<any[]>[]).concat(...results);
        
        return data.map(item => {
            return {
                imo: item.imo,
                vesselName: item.vesselName,
                timestamp: item.timestamp,
                latitude: item.latitude,
                longitude: item.longitude,
                heading: item.heading,
                course: item.course,
                sog: item.sog,
                source: item.source,
                dataSources: item.dataSources,
                readingType: item.readingType,
                navStatus: item.navStatus,
                vesselWidth: item.vesselWidth,
                vesselLength: item.vesselHeight,
                etaScheduled: item.etaScheduled, 
                etaCalc: item.etaCalc, 
                currUnlocode: item.currUnlocode, 
                nextUnlocode: item.nextUnlocode,
                vesselType: item.vesselType,
                vesselClass: item.vesselClass,
            }
        });
        
    }

    static async currentPosition(imoNos: number[]): Promise<VesselPositionData[]> {
        // Size of URL is limited to 8000 bytes, so fetch 100 vessels at a time, more than that and API response is inconsistent
        const imoBatches = [];
        for (let i = 0; i < imoNos.length; i += 100) {
            imoBatches.push(imoNos.slice(i, i + 100));
        }

        const promises = imoBatches.map(imoBatch => {
            const imoQuery = imoBatch.map(imo => "imo=" + imo).join("&");
            return authFetchGET(env.scope, `${env.baseurl}/positions/last/source/last?full=false&${imoQuery}`);
        });

        const results = await Promise.all(promises);
        const data: any[] = (<any[]>[]).concat(...results);

        return data.map(item => {
            return {
                imo: item.imo,
                vesselName: item.vesselName,
                timestamp: item.timestamp,
                latitude: item.latitude,
                longitude: item.longitude,
                heading: item.heading,
                course: item.course,
                sog: item.sog,
                source: item.source,
                dataSources: item.dataSources,
                readingType: item.readingType,
                navStatus: item.navStatus,
                vesselWidth: item.vesselWidth,
                vesselLength: item.vesselHeight,
                etaScheduled: item.etaScheduled,
                etaCalc: item.etaCalc,
                currUnlocode: item.currUnlocode,
                nextUnlocode: item.nextUnlocode,
                vesselType: item.vesselType,
                vesselClass: item.vesselClass,
            }

        });
    }

    static async getLatest(): Promise<VesselPositionData[] | null> {
        try {
            return await authFetchGET(env.scope, `${env.baseurl}/search?n=1000000`);
        } catch (err) {
            console.warn(`NO LATEST POSITIONS DATA`);
            return null;
        }
    }

    // Gets a time range of positions for a specific vessel.
    static async getVesselTrack_VesselVoyage(imoNo: number, fromDate: Date, toDate: Date): Promise<VesselTrackData|null>
    {
        const data: VesselTrackData = await authFetchGET(env.scope, `${env.baseurl}/positions/${imoNo}`,
            {from:fromDate.toISOString(),to:toDate.toISOString()});

        if (!data.positions.length)
        {
            console.warn(`NO POSITIONS DATA for ${imoNo}`);
            return null;
        }

        return data;
    }

     // Gets a time range of positions for a specific vessel.
     static async getVesselTrack(imoNo: number, fromDate: Date, toDate: Date): Promise<VesselPositionDataExtended[]|null>
     {
         const data = await authFetchGET(env.scope, `${env.baseurl}/positions/hist/imo/${imoNo}`,
             {from:fromDate.toISOString(),to:toDate.toISOString()});
             
         if (!data.length)
         {
             console.warn(`NO POSITIONS DATA for ${imoNo}`);
             return null;
         }
 
         return data;
     }

    // Get manual override state
    static async getManualPositionOverride(imoNo: number) {
        const data: {manualPositionOverride: boolean} = await authFetchGET(env.scope, `${env.baseurl}/positions/manualoverride`, { imo: `${imoNo}` });

        return data.manualPositionOverride;
    }

    // Set manual override state
    static async setManualPositionOverride(override: boolean) {
        const data = { manualPositionOverride: override };
        await authFetchPUT(env.scope, `${env.baseurl}/positions/manualoverride`, data);
    }

    // Add manual position
    static async setManualPosition(coordinates: [number, number], timestamp: string) {
        const data = {
            timestamp: timestamp,
            latitude: coordinates[1],
            longitude: coordinates[0]
        };
        await authFetchPOST(env.scope, `${env.baseurl}/positions/manual`, data);
    }
}
